import React, { Fragment, useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Message } from "primereact/message";
import {
  API_URL,
  courseTypeOptions,
  sesiOptions,
  useAxios,
} from "../../../../utils/const";
import { useToast } from "../../../../Context/ToastContext";

const UpdateModal = ({
  toggleModal,
  courseId,
  getCourseSessions,
  courseGroups,
  courseTopics,
  courseLecturers,
  classrooms,
  selectedCourseGroupTimetable,
  courseStudents,
}) => {
  const axiosInstance = useAxios();
  const { setToastObj } = useToast();
  const [loadingModal, setLoadingModal] = useState(false);

  const courseGroupOptions = courseGroups.map((item) => {
    return {
      value: item.id,
      label: item.group.name,
    };
  });
  const defaultCourseGroup = courseGroupOptions.find(
    (option) => option.value === selectedCourseGroupTimetable.courseGroup.id
  );

  const formattedDate = selectedCourseGroupTimetable?.date
    ? new Date(selectedCourseGroupTimetable.date).toISOString().split("T")[0]
    : "";

  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      courseType: {
        value: "",
        label: "",
      },
      courseGroup: defaultCourseGroup || null,
      courseTopic: {
        label: selectedCourseGroupTimetable.courseTopic.topic,
        value: selectedCourseGroupTimetable.courseTopic.id,
      },
      date: formattedDate,
      start_time: selectedCourseGroupTimetable.start_time,
      end_time: selectedCourseGroupTimetable.end_time,
      classroom: {
        label: selectedCourseGroupTimetable.classroom.name,
        value: selectedCourseGroupTimetable.classroom.id,
      },
    },
  });

  useEffect(() => {
    if (selectedCourseGroupTimetable.course_type.includes("TUTORIAL")) {
      setValue("courseType", { value: "TUTORIAL", label: "TUTORIAL" });
      getCourseStudents("TUTORIAL");
      if (selectedCourseGroupTimetable.course_type.includes("SESI 1")) {
        setValue("sesi", { value: 1, label: "1" });
      } else if (selectedCourseGroupTimetable.course_type.includes("SESI 2")) {
        setValue("sesi", { value: 2, label: "2" });
      }
      if (selectedCourseGroupTimetable.lecturers) {
        const lecturers = {
          value: selectedCourseGroupTimetable.lecturers[0].lecturer.id,
          label: selectedCourseGroupTimetable.lecturers[0].lecturer.name,
        };
        setValue("courseLecturers", lecturers);
      }
    } else if (selectedCourseGroupTimetable.course_type === "PERKULIAHAN") {
      setValue("courseType", { value: "PERKULIAHAN", label: "PERKULIAHAN" });
      if (selectedCourseGroupTimetable.lecturers) {
        const lecturers = {
          value: selectedCourseGroupTimetable.lecturers[0].lecturer.id,
          label: selectedCourseGroupTimetable.lecturers[0].lecturer.name,
        };
        setValue("courseLecturers", lecturers);
      }
    } else if (selectedCourseGroupTimetable.course_type === "KKD") {
      setValue("courseType", { value: "KKD", label: "KKD" });
      getCourseStudents("KKD");
      if (selectedCourseGroupTimetable.lecturers) {
        const lecturers = {
          value: selectedCourseGroupTimetable.lecturers[0].lecturer.id,
          label: selectedCourseGroupTimetable.lecturers[0].lecturer.name,
        };
        setValue("courseLecturers", lecturers);
      }
    } else if (selectedCourseGroupTimetable.course_type === "PLENO") {
      setValue("courseType", { value: "PLENO", label: "PLENO" });
      if (selectedCourseGroupTimetable.lecturers) {
        const lecturers = {
          value: selectedCourseGroupTimetable.lecturers[0].lecturer.id,
          label: selectedCourseGroupTimetable.lecturers[0].lecturer.name,
        };
        setValue("courseLecturers", lecturers);
      }
    } else if (selectedCourseGroupTimetable.course_type === "PRAKTIKUM") {
      setValue("courseType", { value: "PRAKTIKUM", label: "PRAKTIKUM" });
      if (selectedCourseGroupTimetable.lecturers) {
        const lecturers = {
          value: selectedCourseGroupTimetable.lecturers[0].lecturer.id,
          label: selectedCourseGroupTimetable.lecturers[0].lecturer.name,
        };
        setValue("courseLecturers", lecturers); // Misalnya "courseLecturers" adalah field untuk memilih dosen
      }
    }
  }, [selectedCourseGroupTimetable, setValue]);

  const getCourseStudents = async (type) => {
    setLoadingModal(true);
    try {
      const response = await axiosInstance.get(
        `${API_URL}/course-sessions/${selectedCourseGroupTimetable.id}/${type}`
      );

      if (type === "TUTORIAL") {
        const studentOptions =
          response.data.data.courseGroupTimetableTutorialStudentAttendances.map(
            (item) => ({
              value: item.student.id,
              label: `${item.student.nim} - ${item.student.name}`,
            })
          );

        setValue("courseStudents", studentOptions);
      } else {
        const studentOptions =
          response.data.data.courseGroupTimetableKkdStudentAttendances.map(
            (item) => ({
              value: item.student.id,
              label: `${item.student.nim} - ${item.student.name}`,
            })
          );
        setValue("courseStudents", studentOptions);
      }

      setLoadingModal(false);
    } catch (error) {
      console.error("Error fetching students:", error);
      setLoadingModal(false);
    }
  };

  const courseType = watch("courseType");

  const [loading, setLoading] = useState(false);

  const courseTopicOptions = courseTopics.courseTopics.map((item) => ({
    value: item.id,
    label: item.topic,
  }));

  const classroomOptions = classrooms.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const courseLecturerOptions = courseLecturers
    .flatMap((item) =>
      item.courseGroupLecturers.map((lecturer) => ({
        value: lecturer.lecturer.id,
        label: lecturer.lecturer.name,
      }))
    )
    .filter(
      (option, index, self) =>
        index === self.findIndex((o) => o.value === option.value)
    );

  const courseStudentOptions = courseStudents.flatMap((item) =>
    item.courseGroupStudents.map((student) => ({
      value: student.student.id,
      label: `${student.student.nim} - ${student.student.name}`,
    }))
  );

  const closeModal = () => {
    toggleModal(false);
  };

  const handleSave = async (formData) => {
    setLoading(true);
    let sesi = null;
    let students = [];

    if (formData.courseType.value === "TUTORIAL") {
      students = formData.courseStudents.map((item) => ({
        student_id: item.value,
      }));
      sesi = formData.sesi.value;
    } else if (formData.courseType.value === "KKD") {
      students = formData.courseStudents.map((item) => ({
        student_id: item.value,
      }));
    }

    const data = {
      course_group_id: formData.courseGroup.value,
      course_type: formData.courseType.value,
      course_topic_id: formData.courseTopic.value,
      date: formData.date,
      start_time: formData.start_time,
      end_time: formData.end_time,
      classroom_id: formData.classroom.value,
      lecturer_id: formData.courseLecturers.value,
      sesi: sesi ? sesi : null,
      students: students,
    };

    try {
      await axiosInstance.put(
        `${API_URL}/course-sessions/${selectedCourseGroupTimetable.id}`,
        data
      );
      getCourseSessions();
      setLoading(false);
      setToastObj({ msg: "Data Berhasil diubah", type: "success" });
      closeModal();
    } catch (error) {
      let errorMessage = "Terjadi kesalahan";
      if (error.response.data.msg != undefined) {
        errorMessage = `Terjadi kesalahan, ${error.response.data.msg}`;
      } else {
        errorMessage = error.message;
      }
      setLoading(false);
      setToastObj({
        msg: errorMessage,
        type: "danger",
      });
      closeModal();
    }
  };

  return (
    <Modal show={toggleModal} onHide={closeModal} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <h6>Ubah Pertemuan</h6>
        </Modal.Title>
      </Modal.Header>

      {loadingModal ? (
        <Modal.Body>
          <div className="row">
            <div
              className="col-md-12 text-center align-center"
              style={{ marginTop: "20%" }}
            >
              <h1>Mohon Tunggu</h1>
            </div>
          </div>
        </Modal.Body>
      ) : (
        <Modal.Body>
          {/* COURSE GROUP */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Kelompok</label>
                <Controller
                  control={control}
                  name="courseGroup"
                  rules={{ required: "-- Pilih Salah Satu --" }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      options={courseGroupOptions}
                      onChange={(value) => field.onChange(value)}
                    />
                  )}
                />
                {errors.courseGroup && (
                  <div className="">
                    <Message
                      className="mt-2 d-flex"
                      severity="error"
                      text={errors.courseGroup.message}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* COURSE TYPE */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Tipe Pertemuan</label>
                <Controller
                  control={control}
                  name="courseType"
                  rules={{ required: "-- Pilih Salah Satu --" }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      options={courseTypeOptions}
                      onChange={(value) => field.onChange(value)}
                      isDisabled={true}
                    />
                  )}
                />
                {errors.courseType && (
                  <div className="">
                    <Message
                      className="mt-2 d-flex"
                      severity="error"
                      text={errors.courseType.message}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* SESI */}
            {courseType.value === "TUTORIAL" && (
              <Fragment>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="form-label">Sesi</label>
                    <Controller
                      control={control}
                      name="sesi"
                      rules={{ required: "-- Pilih Salah Satu --" }}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          options={sesiOptions}
                          onChange={(value) => field.onChange(value)}
                        />
                      )}
                    />
                    {errors.sesi && (
                      <div className="">
                        <Message
                          className="mt-2 d-flex"
                          severity="error"
                          text={errors.sesi.message}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label className="form-label">Kasus</label>
                    <Controller
                      control={control}
                      name="courseTopic"
                      rules={{ required: "-- Pilih Salah Satu --" }}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          options={courseTopicOptions}
                          onChange={(value) => field.onChange(value)}
                        />
                      )}
                    />
                    {errors.courseTopic && (
                      <div className="">
                        <Message
                          className="mt-2 d-flex"
                          severity="error"
                          text={errors.courseTopic.message}
                        />
                      </div>
                    )}
                    <Link
                      to={{ pathname: `/akademik/courses/${courseId}` }}
                      style={{ textDecoration: "none" }}
                    >
                      + Tambah Kasus Baru
                    </Link>
                  </div>
                </div>
              </Fragment>
            )}

            {/* COURSE TOPIC */}
            {courseType.value !== "TUTORIAL" && (
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">Topik</label>
                  <Controller
                    control={control}
                    name="courseTopic"
                    rules={{ required: "-- Pilih Salah Satu --" }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        options={courseTopicOptions}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                  {errors.courseTopic && (
                    <div className="">
                      <Message
                        className="mt-2 d-flex"
                        severity="error"
                        text={errors.courseTopic.message}
                      />
                    </div>
                  )}
                  <Link
                    to={{ pathname: `/akademik/courses/${courseId}` }}
                    style={{ textDecoration: "none" }}
                  >
                    + Tambah Topik Baru
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* DATE */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Tanggal</label>
                <input
                  {...register("date", {
                    required: "Tanggal tidak boleh kosong!",
                  })}
                  type="date"
                  className="form-control"
                />
                {errors.date && (
                  <div className="">
                    <Message
                      className="mt-2 d-flex"
                      severity="error"
                      text={errors.date.message}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* START TIME */}
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Waktu Mulai</label>
                <input
                  {...register("start_time", {
                    required: "Waktu mulai tidak boleh kosong!",
                  })}
                  type="time"
                  className="form-control"
                />
                {errors.start_time && (
                  <div className="">
                    <Message
                      className="mt-2 d-flex"
                      severity="error"
                      text={errors.start_time.message}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* END TIME */}
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Waktu Selesai</label>
                <input
                  {...register("end_time", {
                    required: "Waktu selesai tidak boleh kosong!",
                  })}
                  type="time"
                  className="form-control"
                />
                {errors.end_time && (
                  <div className="">
                    <Message
                      className="mt-2 d-flex"
                      severity="error"
                      text={errors.end_time.message}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* CLASSROOM */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Ruangan</label>
                <Controller
                  control={control}
                  name="classroom"
                  rules={{ required: "-- Pilih Salah Satu --" }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      options={classroomOptions}
                      onChange={(value) => field.onChange(value)}
                    />
                  )}
                />
                {errors.classroom && (
                  <div className="">
                    <Message
                      className="mt-2 d-flex"
                      severity="error"
                      text={errors.classroom.message}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* COURSE GROUP LECTURERS */}
          {courseType.value !== "TUTORIAL" && courseType.value !== "KKD" && (
            <div className="row mb-3">
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">Pengajar</label>
                  <Controller
                    control={control}
                    name="courseLecturers"
                    rules={{ required: "-- Pilih Salah Satu --" }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        options={courseLecturerOptions}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                  {errors.courseLecturers && (
                    <div className="">
                      <Message
                        className="mt-2 d-flex"
                        severity="error"
                        text={errors.courseLecturers.message}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* COURSE GROUP LECTURERS */}
          {courseType.value === "TUTORIAL" && (
            <div className="row mb-3">
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">Fasilitator</label>
                  <Controller
                    control={control}
                    name="courseLecturers"
                    rules={{ required: "-- Pilih Salah Satu --" }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        options={courseLecturerOptions}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                  {errors.courseLecturers && (
                    <div className="">
                      <Message
                        className="mt-2 d-flex"
                        severity="error"
                        text={errors.courseLecturers.message}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">Mahasiswa</label>
                  <Controller
                    control={control}
                    name="courseStudents"
                    rules={{ required: "-- Pilih Salah Satu --" }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        options={courseStudentOptions}
                        isMulti
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                  {errors.courseStudents && (
                    <div className="">
                      <Message
                        className="mt-2 d-flex"
                        severity="error"
                        text={errors.courseStudents.message}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* COURSE GROUP LECTURERS */}
          {courseType.value === "KKD" && (
            <div className="row mb-3">
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">Pengajar</label>
                  <Controller
                    control={control}
                    name="courseLecturers"
                    rules={{ required: "-- Pilih Salah Satu --" }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        options={courseLecturerOptions}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                  {errors.courseLecturers && (
                    <div className="">
                      <Message
                        className="mt-2 d-flex"
                        severity="error"
                        text={errors.courseLecturers.message}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">Mahasiswa</label>
                  <Controller
                    control={control}
                    name="courseStudents"
                    rules={{ required: "-- Pilih Salah Satu --" }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        options={courseStudentOptions}
                        isMulti
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                  {errors.courseStudents && (
                    <div className="">
                      <Message
                        className="mt-2 d-flex"
                        severity="error"
                        text={errors.courseStudents.message}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      )}

      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal}>
          Batal
        </Button>
        <Button
          variant="success"
          disabled={loading}
          onClick={handleSubmit(handleSave)}
        >
          {loading && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          )}
          <span className="ml-1">Simpan</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateModal;
